import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/services/api';
import { NAV } from '@/constants/navigation';
import type { DashboardScopeInfo } from '@/types/api';
import ConnectionStatus from '../ConnectionStatus';
import { ErrorBoundary } from '../ErrorBoundary';

/** Path segments whose following numeric ID is resolved to a real name in the breadcrumb. */
type BreadcrumbEntityKind = 'restaurants' | 'groups';

function useBreadcrumbEntityName(kind: BreadcrumbEntityKind | undefined, id: string): string | undefined {
  const { data: restaurant } = useQuery({
    queryKey: ['restaurant', Number(id)],
    queryFn: () => apiClient.getRestaurant(Number(id)),
    enabled: kind === 'restaurants',
    staleTime: 5 * 60 * 1000,
  });
  const { data: group } = useQuery({
    queryKey: ['group', id],
    queryFn: () => apiClient.getGroup(Number(id)),
    enabled: kind === 'groups',
    staleTime: 5 * 60 * 1000,
  });
  return kind === 'restaurants' ? restaurant?.name : kind === 'groups' ? group?.name : undefined;
}

/**
 * One breadcrumb crumb. A numeric segment directly following `restaurants` or
 * `groups` (e.g. the `42` in `/restaurants/42/tables`) is resolved to that
 * entity's real name instead of showing the bare ID -- falls back to the raw
 * segment (dash-replaced, CSS-capitalized) while the name is loading or for
 * any other path shape.
 */
function BreadcrumbCrumb({
  segment,
  prevSegment,
  to,
  isLast,
}: {
  segment: string;
  prevSegment: string | undefined;
  to: string;
  isLast: boolean;
}) {
  const isId = /^\d+$/.test(segment);
  const kind: BreadcrumbEntityKind | undefined =
    isId && (prevSegment === 'restaurants' || prevSegment === 'groups') ? prevSegment : undefined;
  const resolvedName = useBreadcrumbEntityName(kind, segment);
  const label = resolvedName ?? segment.replace(/-/g, ' ');
  const capitalize = resolvedName ? '' : 'capitalize';

  return isLast ? (
    <span className={`text-gray-900 font-medium ${capitalize}`} aria-current="page">
      {label}
    </span>
  ) : (
    <Link to={to} className={`text-gray-500 hover:text-gray-700 ${capitalize}`}>
      {label}
    </Link>
  );
}

function scopeBadge(scope: DashboardScopeInfo | null): { text: string; className: string } | null {
  if (!scope) return null;
  switch (scope.type) {
    case 'PLATFORM':
      return null;
    case 'EMPTY':
      return { text: 'No access', className: 'bg-red-100 text-red-800 border border-red-200' };
    case 'GROUP':
      return {
        text: scope.groupNames.length === 1 ? `Group: ${scope.groupNames[0]}` : `${scope.groupNames.length} groups`,
        className: 'bg-blue-100 text-blue-800 border border-blue-200',
      };
    case 'RESTAURANT':
      return {
        text: scope.restaurantNames.length === 1 ? `Restaurant: ${scope.restaurantNames[0]}` : `${scope.restaurantNames.length} restaurants`,
        className: 'bg-blue-100 text-blue-800 border border-blue-200',
      };
    case 'MIXED':
      return {
        text: `${scope.groupNames[0] ?? 'Mixed'} + ${scope.restaurantNames.length} restaurants`,
        className: 'bg-blue-100 text-blue-800 border border-blue-200',
      };
    default: {
      // Compile-time exhaustiveness — fail to compile if a new scope.type is added without updating this switch
      const _exhaustive: never = scope.type;
      return _exhaustive;
    }
  }
}

export default function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const permissions = useAuthStore((s) => s.permissions);
  const scope = useAuthStore((s) => s.scope);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const visibleNav = NAV.filter((item) => permissions.has(item.permission));
  const badge = scopeBadge(scope);

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    // The Reports nav links to /reports/sales, but should stay active on any /reports/* sub-page.
    if (path.startsWith('/reports/')) {
      return location.pathname.startsWith('/reports');
    }
    return location.pathname.startsWith(path);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-0 focus:left-0 focus:z-50 focus:p-4 focus:bg-white focus:text-primary-600"
      >
        Skip to main content
      </a>

      {/* Top Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              {/* Logo */}
              <div className="flex-shrink-0 flex items-center">
                <Link to="/" className="flex items-center" aria-label="Time2Dine Home">
                  <svg
                    className="h-8 w-8 text-primary-600"
                    viewBox="0 0 32 32"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M16 2C8.268 2 2 8.268 2 16s6.268 14 14 14 14-6.268 14-14S23.732 2 16 2zm0 25c-1.1 0-2-.9-2-2h4c0 1.1-.9 2-2 2zm6-3H10v-2h12v2zm-.1-4H10.1c-.1-.3-.1-.7-.1-1 0-3.3 2.7-6 6-6s6 2.7 6 6c0 .3 0 .7-.1 1z" />
                    <circle cx="16" cy="10" r="2" />
                  </svg>
                  <span className="ml-2 text-xl font-bold text-primary-600">
                    Time2Dine
                  </span>
                </Link>
              </div>

              {/* Desktop Navigation */}
              <div className="hidden md:ml-6 md:flex md:space-x-4 lg:space-x-8">
                {visibleNav.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors min-h-[44px] ${
                      isActive(item.href)
                        ? 'border-primary-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }`}
                    aria-current={isActive(item.href) ? 'page' : undefined}
                  >
                    <span className="mr-2" aria-hidden="true">{item.icon}</span>
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* User Menu - Desktop */}
            <div className="hidden md:flex md:items-center">
              <div className="flex items-center space-x-4">
                <ConnectionStatus />
                {badge && (
                  <span
                    data-testid="scope-badge"
                    className={`px-2 py-0.5 rounded text-xs font-medium ${badge.className}`}
                  >
                    {badge.text}
                  </span>
                )}
                <span className="text-sm text-gray-700">
                  {user?.firstName} {user?.lastName}
                </span>
                <button
                  onClick={handleLogout}
                  className="btn btn-outline min-h-[44px] min-w-[80px] text-sm"
                  aria-label="Log out of your account"
                >
                  Logout
                </button>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center md:hidden">
              <button
                type="button"
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 min-h-[44px] min-w-[44px]"
                aria-controls="mobile-menu"
                aria-expanded={mobileMenuOpen}
                aria-label={mobileMenuOpen ? 'Close main menu' : 'Open main menu'}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={`md:hidden transition-all duration-200 ease-in-out ${
            mobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
          }`}
          id="mobile-menu"
        >
          <div className="pt-2 pb-3 space-y-1 border-t border-gray-200">
            {visibleNav.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={closeMobileMenu}
                className={`block pl-3 pr-4 py-3 border-l-4 text-base font-medium min-h-[48px] ${
                  isActive(item.href)
                    ? 'bg-primary-50 border-primary-500 text-primary-700'
                    : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                }`}
                aria-current={isActive(item.href) ? 'page' : undefined}
              >
                <span className="mr-3" aria-hidden="true">{item.icon}</span>
                {item.name}
              </Link>
            ))}
          </div>
          <div className="pt-4 pb-3 border-t border-gray-200">
            <div className="flex items-center px-4">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-600 font-medium">
                    {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                  </span>
                </div>
              </div>
              <div className="ml-3 flex-1">
                <div className="text-base font-medium text-gray-800">
                  {user?.firstName} {user?.lastName}
                </div>
                <div className="text-sm font-medium text-gray-500">
                  {user?.email}
                </div>
                {badge && (
                  <span
                    data-testid="scope-badge"
                    className={`mt-1 inline-block px-2 py-0.5 rounded text-xs font-medium ${badge.className}`}
                  >
                    {badge.text}
                  </span>
                )}
              </div>
              <ConnectionStatus />
            </div>
            <div className="mt-3 px-2">
              <button
                onClick={() => {
                  closeMobileMenu();
                  handleLogout();
                }}
                className="block w-full text-left px-3 py-3 rounded-md text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 min-h-[48px]"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Breadcrumb - shows current location */}
      {location.pathname !== '/' && (
        <nav className="bg-white border-b border-gray-200" aria-label="Breadcrumb">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <ol className="flex items-center space-x-2 py-3 text-sm">
              <li>
                <Link to="/" className="text-gray-500 hover:text-gray-700">
                  Home
                </Link>
              </li>
              {location.pathname.split('/').filter(Boolean).map((segment, index, arr) => (
                <li key={`${index}-${segment}`} className="flex items-center">
                  <svg className="h-4 w-4 text-gray-400 mx-2" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <BreadcrumbCrumb
                    segment={segment}
                    prevSegment={arr[index - 1]}
                    to={`/${arr.slice(0, index + 1).join('/')}`}
                    isLast={index === arr.length - 1}
                  />
                </li>
              ))}
            </ol>
          </div>
        </nav>
      )}

      {/* Main Content */}
      <main id="main-content" className="py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Keyed by path so navigating to a new page resets a previous crash (C1 fix). */}
          <ErrorBoundary key={location.pathname} label={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
